/**
 * Mobile Progress Note Page
 * Allows creating progress notes offline with auto-sync
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { fetchAllPages } from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, Smile, Meh, Frown, Clock, CheckCircle, Camera, Target, Sparkles, FileText, Check, X } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES, ACTION_STATUS, getQueueStats } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAttachmentCapture from '@/components/mobile/MobileAttachmentCapture';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';
import { ATTACHMENT_ENTITY_TYPES } from '@/lib/offline/attachmentStorage';

const NOTE_TYPES = [
  { value: 'GENERAL', label: 'General Note' },
  { value: 'DAILY_SUMMARY', label: 'Daily Summary' },
  { value: 'ACTIVITY', label: 'Activity Record' },
  { value: 'HEALTH', label: 'Health Observation' },
  { value: 'BEHAVIOR', label: 'Behavior Note' },
  { value: 'COMMUNICATION', label: 'Communication' },
];

const MOOD_OPTIONS = [
  { value: 'HAPPY', label: 'Happy', icon: Smile, color: 'text-green-500' },
  { value: 'NEUTRAL', label: 'Neutral', icon: Meh, color: 'text-amber-500' },
  { value: 'SAD', label: 'Sad', icon: Frown, color: 'text-red-500' },
];

const ACTIVITY_OPTIONS = [
  'Personal Care', 'Meal Preparation', 'Medication Support', 'Community Access',
  'Exercise', 'Social Activity', 'Domestic Tasks', 'Transport', 'Other'
];

const PROGRESS_LEVELS = [
  { value: 'ACHIEVED', label: 'Achieved', color: 'bg-emerald-600' },
  { value: 'PARTIAL', label: 'Partial', color: 'bg-amber-500' },
  { value: 'NO_PROGRESS', label: 'No progress', color: 'bg-slate-500' },
  { value: 'CONCERN', label: 'Concern', color: 'bg-red-600' },
  { value: 'NOT_APPLICABLE', label: 'N/A', color: 'bg-slate-400' },
];

export default function MobileProgressNotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');
  const shiftId = searchParams.get('shiftId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [form, setForm] = useState({
    participantId: participantId || '',
    shiftId: shiftId || '',
    noteType: 'GENERAL',
    content: '',
    mood: '',
    activities: [],
    goalId: '',
    progressLevel: '',
    supportProvided: '',
    outcomeObserved: '',
    concern: '',
    followUpRequired: false,
    templateKey: '',
    templateName: '',
  });
  const [attachments, setAttachments] = useState([]);
  const [goals, setGoals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMeta, setAiMeta] = useState(null);

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants for dropdown
  useEffect(() => {
    if (online) {
      fetchAllPages('/participants')   // NEW-007: every assigned participant, not just the first 100
        .then(items => setParticipants(items || []))
        .catch(() => {});
    }
  }, [online]);

  // Day 24 — load note templates
  useEffect(() => {
    api.get('/mobile/note-templates')
      .then(res => setTemplates(res.data?.data || []))
      .catch(() => {});
  }, []);

  // Day 23 — load goals scoped to the selected participant
  useEffect(() => {
    if (!form.participantId) { setGoals([]); return; }
    api.get(`/mobile/participants/${form.participantId}/goals`)
      .then(res => setGoals(res.data?.data || []))
      .catch(() => setGoals([]));
  }, [form.participantId]);

  const applyTemplate = (key) => {
    const t = templates.find(x => String(x.key) === String(key));
    if (!t) return;
    const f = t.fields || {};
    setForm(prev => ({
      ...prev,
      noteType: t.noteType || prev.noteType,
      templateKey: t.key,
      templateName: t.name,
      content: f.content !== undefined ? f.content : prev.content,
      supportProvided: f.supportProvided !== undefined ? f.supportProvided : prev.supportProvided,
      outcomeObserved: f.outcomeObserved !== undefined ? f.outcomeObserved : prev.outcomeObserved,
      concern: f.concern !== undefined ? f.concern : prev.concern,
    }));
    toast.success(`Template "${t.name}" applied — edit before saving`);
  };

  const handleAiImprove = async () => {
    if (!form.content.trim() || form.content.trim().length < 3) {
      toast.error('Write a draft note first');
      return;
    }
    setAiLoading(true);
    try {
      const res = await api.post('/mobile/notes/ai-improve', {
        text: form.content, noteType: form.noteType,
      });
      const d = res.data?.data;
      setAiSuggestion(d);
    } catch (e) {
      toast.error('AI improve unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const acceptAiSuggestion = () => {
    if (!aiSuggestion) return;
    setForm(prev => ({ ...prev, content: aiSuggestion.suggestion }));
    setAiMeta({ aiAssisted: true, aiModel: aiSuggestion.model });
    setAiSuggestion(null);
    toast.success('AI suggestion applied — review before saving');
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleActivity = (activity) => {
    setForm(prev => ({
      ...prev,
      activities: prev.activities.includes(activity)
        ? prev.activities.filter(a => a !== activity)
        : [...prev.activities, activity]
    }));
  };

  const saveDraft = async () => {
    if (!form.content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      const action = await queueOfflineAction(ACTION_TYPES.PROGRESS_NOTE, {
        participantId: form.participantId,
        shiftId: form.shiftId,
        noteType: form.noteType,
        content: form.content,
        mood: form.mood || null,
        activities: form.activities,
        timestamp: new Date().toISOString(),
        attachmentIds: attachments.map(a => a.id),
        // Day 23 — goal linkage + structured outcomes
        goalId: form.goalId || null,
        progressLevel: form.progressLevel || null,
        supportProvided: form.supportProvided || null,
        outcomeObserved: form.outcomeObserved || null,
        concern: form.concern || null,
        followUpRequired: form.followUpRequired,
        // Day 24 — template metadata
        templateKey: form.templateKey || null,
        templateName: form.templateName || null,
        // Day 25 — AI-assist metadata only
        aiAssisted: aiMeta?.aiAssisted || false,
        aiModel: aiMeta?.aiModel || null,
      });

      toast.success('Note saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.forbidden > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
            setAccessDenied(true);
          } else if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Note synced successfully');
            setTimeout(() => navigate(-1), 1000);
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        // Will sync later when online
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save note');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <>
    {accessDenied && <MobileAccessDenied />}
    <div className={`min-h-screen bg-background pb-24 ${accessDenied ? 'hidden' : ''}`} data-testid="mobile-progress-note-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">New Progress Note</h1>
            <p className="text-xs text-muted-foreground">Record participant progress</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Participant Selection */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="participant">Participant</Label>
              <SearchSelect
                options={participants.map(p => ({ value: p.id, label: participantLabel(p), keywords: p.ndis_number || '' }))}
                value={form.participantId}
                onChange={(v) => handleChange('participantId', v)}
                placeholder="Select participant"
                searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                emptyText="No participants match that search"
                className="h-11"
                testId="participant-select"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="noteType">Note Type</Label>
              <Select 
                value={form.noteType} 
                onValueChange={(v) => handleChange('noteType', v)}
              >
                <SelectTrigger id="noteType" data-testid="note-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Day 24 — Template selector */}
            <div className="space-y-2">
              <Label htmlFor="template" className="flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" /> Start from a template (optional)
              </Label>
              <Select value={form.templateKey || undefined} onValueChange={applyTemplate}>
                <SelectTrigger id="template" data-testid="note-template-select">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.key} value={String(t.key)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.templateName && (
                <p className="text-[11px] text-muted-foreground">Template applied: {form.templateName} (editable)</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Day 23 — Goal linkage + structured outcomes */}
        <Card data-testid="goal-link-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-emerald-600" /> Goal &amp; Outcome (optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="goal">Linked goal</Label>
              <Select
                value={form.goalId || undefined}
                onValueChange={(v) => handleChange('goalId', v === '__none' ? '' : v)}
                disabled={!form.participantId}
              >
                <SelectTrigger id="goal" data-testid="goal-select">
                  <SelectValue placeholder={form.participantId ? 'Select a goal' : 'Select participant first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No goal</SelectItem>
                  {goals.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.participantId && goals.length === 0 && (
                <p className="text-[11px] text-muted-foreground">No active goals for this participant.</p>
              )}
            </div>

            {form.goalId && (
              <div className="space-y-2" data-testid="progress-level-group">
                <Label>Progress level</Label>
                <div className="flex flex-wrap gap-2">
                  {PROGRESS_LEVELS.map(pl => (
                    <button
                      key={pl.value}
                      type="button"
                      onClick={() => handleChange('progressLevel', form.progressLevel === pl.value ? '' : pl.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        form.progressLevel === pl.value ? `${pl.color} text-white border-transparent` : 'bg-muted hover:bg-muted/80 border-transparent'
                      }`}
                      data-testid={`progress-${pl.value.toLowerCase()}`}
                    >
                      {pl.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="supportProvided">Support provided</Label>
              <Textarea id="supportProvided" rows={2} className="resize-none"
                placeholder="What support did you provide?"
                value={form.supportProvided}
                onChange={(e) => handleChange('supportProvided', e.target.value)}
                data-testid="support-provided-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="outcomeObserved">Outcome observed</Label>
              <Textarea id="outcomeObserved" rows={2} className="resize-none"
                placeholder="What did you observe?"
                value={form.outcomeObserved}
                onChange={(e) => handleChange('outcomeObserved', e.target.value)}
                data-testid="outcome-observed-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="concern">Concern (if any)</Label>
              <Textarea id="concern" rows={2} className="resize-none"
                placeholder="Any concerns to flag?"
                value={form.concern}
                onChange={(e) => handleChange('concern', e.target.value)}
                data-testid="concern-input" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="followUp" checked={form.followUpRequired}
                onCheckedChange={(v) => handleChange('followUpRequired', !!v)}
                data-testid="follow-up-checkbox" />
              <Label htmlFor="followUp" className="cursor-pointer">Follow-up required</Label>
            </div>
          </CardContent>
        </Card>

        {/* Mood Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Participant Mood</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex gap-2">
              {MOOD_OPTIONS.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange('mood', form.mood === value ? '' : value)}
                  className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                    form.mood === value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-transparent bg-muted/50 hover:bg-muted'
                  }`}
                  data-testid={`mood-${value.toLowerCase()}`}
                >
                  <Icon className={`h-6 w-6 ${color}`} />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Activities */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Activities (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex flex-wrap gap-2">
              {ACTIVITY_OPTIONS.map(activity => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => toggleActivity(activity)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    form.activities.includes(activity)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  data-testid={`activity-${activity.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {activity}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Note Content */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span>Note Content *</span>
              <Button type="button" variant="outline" size="sm" className="gap-1 h-7"
                onClick={handleAiImprove} disabled={aiLoading || !form.content.trim()}
                data-testid="ai-improve-btn">
                <Sparkles className="h-3.5 w-3.5" /> {aiLoading ? 'Improving…' : 'AI improve'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="content"
              placeholder="Enter your progress note here..."
              value={form.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={6}
              className="resize-none"
              data-testid="note-content"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {form.content.length} characters
              {aiMeta?.aiAssisted && <span className="ml-2 text-primary">· AI-assisted</span>}
            </p>

            {aiSuggestion && (
              <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="ai-suggestion-panel">
                <div className="flex items-center gap-1 text-xs font-semibold text-primary">
                  <Sparkles className="h-3.5 w-3.5" /> Suggested wording (mock) — review before using
                </div>
                <p className="text-sm whitespace-pre-wrap">{aiSuggestion.suggestion}</p>
                <p className="text-[11px] text-muted-foreground">{aiSuggestion.disclaimer}</p>
                <div className="flex gap-2 pt-1">
                  <Button type="button" size="sm" className="gap-1" onClick={acceptAiSuggestion} data-testid="ai-accept-btn">
                    <Check className="h-3.5 w-3.5" /> Use suggestion
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="gap-1" onClick={() => setAiSuggestion(null)} data-testid="ai-reject-btn">
                    <X className="h-3.5 w-3.5" /> Keep mine
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Photos / Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <MobileAttachmentCapture
              entityType={ATTACHMENT_ENTITY_TYPES.PROGRESS_NOTE}
              entityId={null}
              maxAttachments={5}
              acceptedTypes="image/*,application/pdf"
              onAttachmentsChange={setAttachments}
              initialAttachments={attachments}
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1"
            onClick={saveDraft}
            disabled={saving || !form.content.trim()}
            data-testid="save-note-btn"
          >
            {saving ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {online ? 'Save & Sync' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
